<template>
  <div>
    <!-- Toolbar -->
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="hidden-sm-and-down">
        {{ $t("referral_program.title") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-app-bar>

    <v-container fluid class="referral-page pa-5">
    <!-- Hero Card -->
    <v-card flat class="referral-hero-card mb-5 pa-5" rounded="lg" border>
      <div class="d-flex align-center gap-4">
        <div class="referral-hero-icon flex-shrink-0">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#534AB7"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <div>
          <p class="font-weight-medium text-body-1 mb-1">
            {{ $t("referral_program.hero_heading") }}
          </p>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ $t("referral_program.hero_description") }}
          </p>
        </div>
      </div>
    </v-card>

    <!-- Stats Row -->
    <v-row class="mb-5 d-flex justify-space-between" dense>
      <v-col cols="12" sm="4">
        <v-card flat color="grey-lighten-4" rounded="lg" class="pa-4">
          <p class="text-caption text-medium-emphasis mb-1">{{ $t("referral_program.stat_total") }}</p>
          <p class="text-h4 font-weight-medium mb-0">
            {{ stats.total }}
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Referrals List Card -->
    <v-card flat rounded="lg" border>
      <!-- List Header -->
      <div class="referral-list-header d-flex justify-space-between align-center px-4 py-3">
        <span class="text-body-2 font-weight-medium">{{ $t("referral_program.list_title") }}</span>
        <span class="text-caption text-medium-emphasis">{{ $t("referral_program.list_sorted_by_date") }}</span>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="d-flex justify-center align-center pa-8">
        <v-progress-circular indeterminate color="red-darken-3" size="40" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!loading && referrals.length === 0"
        class="d-flex flex-column align-center justify-center pa-8 text-medium-emphasis"
      >
        <v-icon size="48" class="mb-3">mdi-account-multiple-outline</v-icon>
        <p class="text-body-2">{{ $t("referral_program.empty_state") }}</p>
      </div>

      <!-- Referral Items -->
      <template v-else>
        <div
          v-for="(referral, index) in referrals"
          :key="referral._id"
          class="referral-item d-flex align-center gap-3 px-4 py-4"
          :class="{ 'referral-item-border': index < referrals.length - 1 }"
        >
          <!-- Logo / Initials Avatar -->
          <div class="referral-avatar flex-shrink-0 d-flex align-center justify-center">
            <img
              v-if="referral.logo"
              :src="`https://leko-tech.fra1.cdn.digitaloceanspaces.com/${referral.logo}`"
              :alt="referral.title"
              class="referral-logo"
              @error="referral.logo = null"
            />
            <span
              v-else
              class="font-weight-medium"
              :style="getAvatarStyle(referral.title)"
              style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;border-radius:50%;"
            >
              {{ getInitials(referral.title) }}
            </span>
          </div>

          <!-- Info -->
          <div class="flex-grow-1 min-width-0">
            <p class="font-weight-medium text-body-2 mb-0">{{ referral.title }}</p>
            <p class="text-caption text-medium-emphasis mb-0">
              {{ referral.city }}
              <template v-if="referral.createdAt">
                &middot; {{ formatDate(referral.createdAt) }}
              </template>
            </p>
          </div>

          <!-- Status Badge -->
          <span
            class="referral-badge flex-shrink-0 text-caption badge-active"
          >
            {{ $t("referral_program.status_active") }}
          </span>
        </div>
      </template>
    </v-card>

    <!-- Footer Note -->
    <h3 class=" text-center text-medium-emphasis mt-5 mb-0">
      <strong>{{ $t("referral_program.footer_note") }}</strong>
   </h3>
    </v-container>
  </div>
</template>

<script>
import getRequest from "../api/getRequest";

const AVATAR_PALETTES = [
  { bg: "#E6F1FB", color: "#0C447C" },
  { bg: "#EEEDFE", color: "#3C3489" },
  { bg: "#FAECE7", color: "#712B13" },
  { bg: "#FAEEDA", color: "#633806" },
  { bg: "#E1F5EE", color: "#085041" },
  { bg: "#FDE8F0", color: "#7C1040" },
];

export default {
  name: "ReferralProgram",

  data() {
    return {
      loading: false,
      referrals: [],
    };
  },

  computed: {
    stats() {
      return { total: this.referrals.length };
    },
  },

  mounted() {
    this.fetchReferrals();
  },

  methods: {
    fetchReferrals() {
      this.loading = true;
      const institutionId = this.$store.getters.institutionId;
      getRequest("/institution/referrals", `institution_id=${institutionId}`)
        .then((response) => {
          this.referrals = response.institutions;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },

    getInitials(title) {
      if (!title) return "?";
      const words = title.trim().split(/\s+/);
      if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
      return (words[0][0] + words[1][0]).toUpperCase();
    },

    getAvatarStyle(title) {
      let hash = 0;
      for (let i = 0; i < (title || "").length; i++) {
        hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
      }
      const palette = AVATAR_PALETTES[hash % AVATAR_PALETTES.length];
      return { backgroundColor: palette.bg, color: palette.color };
    },

    formatDate(isoString) {
      const date = new Date(isoString);
      const opts = { day: "numeric", month: "short", year: "numeric" };
      const locale = this.$i18n.locale || "it";
      const formatted = date.toLocaleDateString(locale, opts);
      return `${this.$t("referral_program.date_joined")} ${formatted}`;
    },
  },
};
</script>

<style scoped>
.referral-page {
  max-width: 860px;
}

.referral-hero-card {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.referral-hero-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eeedfe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-active {
  color: #0f6e56;
}

.stat-pending {
  color: #ba7517;
}

.referral-list-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.referral-item {
  gap: 14px;
}

.referral-item-border {
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.referral-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 13px;
  line-height: 1;
  overflow: hidden;
}

.referral-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
}

.min-width-0 {
  min-width: 0;
}

.referral-badge {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
}

.badge-active {
  background: #e1f5ee;
  color: #085041;
}

.badge-pending {
  background: #faeeda;
  color: #633806;
}
</style>
